// src/context/WebSocketContext.tsx

import React, { createContext, useContext, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

export interface WebSocketEvent {
  name: string;
  data: any;
}

const wsUrl = process.env.REACT_APP_WEBSOCKET_URL!;
const apiKey = process.env.REACT_APP_API_GATEWAY_KEY!;

interface WebSocketContextType {
  connection: WebSocket | null;
  open: () => Promise<void>;
  register: (tools: string, handler: (event: any) => void) => void;
  unregister: (tools: string, handler: (event: any) => void) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connection, setConnection] = useState<WebSocket | null>(null);
  const handlers = useRef<{ [key: string]: (event: WebSocketEvent) => void }>(
    {}
  );
  const { session } = useAuth();
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const maxReconnectAttempts = 5;

  const open = async () => {
    if (connecting) {
      return;
    }
    setConnecting(true);
    if (!session) {
      console.error(
        "No session found. WebSocket connection cannot be established."
      );
      return;
    }
    const idToken = session.tokens!.idToken!.toString();
    const protocol = btoa(JSON.stringify({ id: idToken, key: apiKey })).replace(
      /^=+|=+$/g,
      ""
    );

    console.log("WebSocket connecting.");
    const ws = new WebSocket(`${wsUrl}`, protocol);

    ws.onopen = () => {
      console.log("WebSocket connection established.");
      setConnection(ws);
      setReconnectAttempts(0); // Reset reconnect attempts on successful connection
      setConnecting(false);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const tool = data.tool;
      console.log("WebSocket message:", data);
      // debugger;
      if (tool in handlers.current) {
        handlers.current[tool](data.event);
      } else {
        console.warn("No handler registered for tool:", tool);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnecting(false);
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed.");
      setConnection(null);
      setConnecting(false);
      if (reconnectAttempts < maxReconnectAttempts) {
        setTimeout(() => {
          setReconnectAttempts((prev) => prev + 1);
          open();
        }, 1000 * Math.pow(2, reconnectAttempts));
      } else {
        console.error("Max reconnect attempts reached. Giving up.");
      }
    };
  };

  const register = (tool: string, handler: (event: any) => void) => {
    if (connection === null) {
      open();
    }
    if (!(tool in handlers.current)) {
      console.log("Websocket: Registering handler for tool:", tool);
      handlers.current[tool] = handler;
    }
  };

  const unregister = (tool: string, handler: (event: any) => void) => {
    if (tool in handlers.current) {
      console.log("Websocket: Unregistering handler for tool:", tool);
      delete handlers.current[tool];
    }
  };

  //   useEffect(() => {
  //     return () => {
  //       console.log("WebSocket cleanup");
  //       if (connection) {
  //         setReconnectAttempts(maxReconnectAttempts);
  //         connection.close();
  //       }
  //     };
  //   }, [connection]);

  return (
    <WebSocketContext.Provider value={{ connection, open, register, unregister }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
